<template>
  <v-row>
    <v-col cols="12">
      <InputField
        @input="handleTitleInput"
        mandatory
        :value="inputTitle"
        :placeholder="t$('.enter_title')"
        :label="t$('.title')"
        :rules="inputTitleValidation"
        id="topic-form__titleInput"
      />
    </v-col>
    <v-col cols="12">
      <Subtitle :fontSize="1.1" :fontWeight="900"> Gruppe</Subtitle>

      <TextBody customClass="mb-0">
        Deine Diskussion wird erstellt in:
      </TextBody>
      <GroupSelect
        :org-level="inputLevelOrClient"
        :org-level-options="levelOrClientOptions"
        :org-level-label="t$('.selectedLevelOrClient')"
        :group="inputGroup"
        :is-group-page="isGroupPage"
        :queryParams="{ forum: true }"
        :group-label="t$('.group')"
        @groupChange="handleGroupInput"
        :is-edit="isEdit"
        :input-group-validation="inputGroupValidation"
        @orgLevelChange="handleLevelOrClientInput"
        @updateLevelSelection="updateTags"
      />
    </v-col>
    <v-col cols="12">
      <div
        v-if="onlyConfidentialAvailable"
        style="margin-bottom: 16px; font-style: italic"
      >
        Diese Gruppe ist vertraulich. Nur Mitglieder können auf diese Diskussion
        zugreifen.
      </div>
      <ContentfulSelect
        v-else
        :options="accessOptions"
        @input="handleAccessInput"
        :value="inputAccessVal"
        item-value="type"
        :label="t$('.access_and_visibility')"
        no-data-text="Bitte wähle zuerst eine Gruppe."
      />
    </v-col>
    <v-col cols="12">
      <Subtitle :fontSize="1.1" :fontWeight="900">
        {{ t$(".tags") }}
      </Subtitle>
      <TextBody>
        {{ t$(".tagsInfoText") }}
      </TextBody>
      <ChipSelectCombo
        :label="t$('.tags')"
        @input="handleTagsInput"
        :value="inputTags"
        :options="tagsOptions"
        :noDataHTMLText="t$('.noTagsFoundDefaultText')"
      />
    </v-col>
  </v-row>
</template>

<script lang="ts">
import { mdiChevronRight } from "@mdi/js"
import Header from "../../UI/Header.vue"
import InputField from "../../../app/ui-primitives/forms/InputField.vue"
import ContentfulSelect, {
  ContentfulOption,
} from "components/UI/ContentfulSelect/index.vue"
import FieldLabel from "../../UI/FieldLabel.vue"
import TextBody from "../../UI/TextBody.vue"
import Subtitle from "../../UI/Subtitle.vue"
import Hyperlink from "../../UI/Hyperlink.vue"
import ChipSelectCombo from "../../../app/ui-primitives/forms/ChipSelectCombo.vue"

import {
  ref,
  computed,
  PropType,
  Ref,
  getCurrentInstance,
  ComputedRef,
  defineComponent,
} from "vue"
import { useAccessOptions } from "components/TopicForm/general-fields/use-access-options"
import { Client, Group, Level } from "suus-api"
import GroupSelect from "features/common/forms/form-fields/GroupSelect.vue"
import { GroupAccess } from "components/TopicForm/general-fields/group-access"
import useTranslate from "features/common/translate/concerns/useTranslate"
import { tagsApi } from "store/api"
import { useGroupContext } from "features/group/groupContext"

type LevelOrClient = { type: "Level" | "Client"; value: Level | Client }

export default defineComponent({
  name: "TopicFormPanelGeneralFields",
  components: {
    GroupSelect,
    Header,
    InputField,
    ContentfulSelect,
    ChipSelectCombo,
    FieldLabel,
    Subtitle,
    TextBody,
    Hyperlink,
  },
  props: {
    inputTitle: { type: String, default: "" },
    inputGroup: { type: Object as PropType<Group>, default: null },
    inputLevelOrClient: {
      type: Object as PropType<LevelOrClient | null>,
      default: null,
    },
    inputAccessVal: { type: String as PropType<GroupAccess>, default: null },
    inputTags: { type: Array as PropType<string[]>, default: () => [] },
    levelOrClientOptions: {
      type: Array as PropType<LevelOrClient[]>,
      default: () => [],
    },
    inputTitleValidation: { type: Array as PropType<any[]>, default: () => [] },
    inputGroupValidation: { type: Array as PropType<any[]>, default: () => [] },
    isEdit: { type: Boolean, default: false },
  },
  setup(props, context) {
    const root = getCurrentInstance().proxy

    const groupContext = useGroupContext()
    const isGroupPage = groupContext.isGroupAvailable

    const buildHandler = (key: string) => (val: any) =>
      context.emit("update-field", { key, val })

    // Tags Select Options
    let tagsOptions = ref([])

    function composeListOfTagNames(tagsList) {
      return tagsList.map((tag) => tag.name)
    }

    const lastSelectedLevelId = ref(null)

    function updateTags({ selectedLevelId, selectedClientId }) {
      if (selectedLevelId !== lastSelectedLevelId.value) {
        lastSelectedLevelId.value = selectedLevelId
        tagsApi.getTags(selectedClientId, selectedLevelId).then((res) => {
          const tags = res.data
          tagsOptions.value = composeListOfTagNames(tags)
        })
      }
    }

    const handleAccessInput = buildHandler("inputAccessVal")

    const memberships = computed(() => {
      return root.$store.state.member?.currentMember?.data
        ?.group_memberships as number[]
    })
    const _selectedGroup: ComputedRef<Group> = computed(() => props.inputGroup)
    const accessOptions: Ref<ContentfulOption[]> = useAccessOptions(
      _selectedGroup,
      memberships,
      computed(() => props.inputAccessVal),
      (access) => {
        handleAccessInput(access)
      }
    )

    const { t$ } = useTranslate("components.topic_form")

    const onlyConfidentialAvailable = computed(() => {
      return (
        accessOptions.value.length === 1 &&
        accessOptions.value[0].type === GroupAccess.CONFIDENTIAL
      )
    })

    return {
      handleTitleInput: buildHandler("inputTitle"),
      handleTagsInput: buildHandler("inputTags"),
      handleGroupInput: buildHandler("inputGroup"),
      handleLevelOrClientInput: buildHandler("inputLevelOrClient"),
      handleAccessInput,
      accessOptions,
      tagsOptions,
      isGroupPage,
      updateTags,
      t$,
      onlyConfidentialAvailable,
    }
  },
  data() {
    return {
      chevron: mdiChevronRight,
    }
  },
})
</script>
